import useAPI from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import axios, { Method } from "axios";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export interface AuthRedirectProps {
  to: string;
  body?: any;
  method?: Method;
  onDone?: () => void;
  redirectTo?: string;
}

const TOKEN_REFRESH_WINDOW_MS = 10 * 60 * 1000;
const FOLLOW_UP_REFRESH_DELAYS_MS = [5_000, 15_000, 30_000, 60_000];

const errorMessageKey = (status?: number): string => {
  if (status === 409) return "authRedirect.alreadyConnected";
  if (status === 503) return "authRedirect.unavailable";
  return "authRedirect.error";
};

const AuthRedirect: React.FC<AuthRedirectProps> = ({
  to,
  body = [],
  method = "GET",
  onDone = () => {},
  redirectTo = "/"
}) => {
  const { data, error } = useAPI(to, { method: method, data: body });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const redirectedRef = useRef(false); // Added to prevent duplicate redirects
  const errorHandledRef = useRef(false);

  useEffect(() => {
    if (error && !errorHandledRef.current) {
      errorHandledRef.current = true;
      // Auth failures are plain Errors and already route through re-login.
      if (axios.isAxiosError(error)) {
        toast.error(t(errorMessageKey(error.response?.status)));
      }
      onDone();
    }
  }, [error, onDone, t]);

  useEffect(() => {
    if (data?.redirectUrl && !redirectedRef.current) {
      redirectedRef.current = true; // Ensure redirect happens only once
      window.open(
        data.redirectUrl,
        "_blank",
        "width=600,height=900,menubar=no,toolbar=no,location=no,status=no"
      );

      // The window can regain focus before the provider callback has stored the
      // token, so refresh on every return and again shortly after it, until the
      // flow has had time to finish. This outlives the component, which unmounts
      // via onDone right after opening the window.
      const refreshTokens = () => {
        queryClient.invalidateQueries({
          queryKey: ["settings/tokens/all"]
        });
      };
      let followUps: number[] = [];
      const handleFocus = () => {
        followUps.forEach((id) => window.clearTimeout(id));
        refreshTokens();
        followUps = FOLLOW_UP_REFRESH_DELAYS_MS.map((delay) =>
          window.setTimeout(refreshTokens, delay)
        );
      };
      window.addEventListener("focus", handleFocus);
      window.setTimeout(() => {
        window.removeEventListener("focus", handleFocus);
        followUps.forEach((id) => window.clearTimeout(id));
      }, TOKEN_REFRESH_WINDOW_MS);

      navigate(redirectTo);
      onDone();
    }
  }, [data, method, body, navigate, onDone, redirectTo, queryClient]);

  return null;
};

export default AuthRedirect;
